import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Card, CardContent, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import util from '../util/index';
import strings from '../Globalization/index';
import NextPrevComponent from '../components/common/NextPrevComponent';
import CycleInfoProvider from '../contexts/CycleInfo';
import { useCycleInfo } from '../contexts/CycleInfo/Context';
import CycleTape from '../contexts/CycleInfo/CycleTape';
import { usePrerequisites } from '../contexts/Prerequisites';
import { getSearchParams } from '../contexts/Prerequisites/RoutesGuard';
import { useSetState, useUniqueEffect } from '../hooks';
import connectionService from '../services/Connection';
import TopSection from './enspirecycleinfoviews/TopSection';
import TrendsLineChart from './endosystemcycleinfoviews/TrendsLineChart';
import CriticalParametersTable from './enspirecycleinfoviews/CritcalParametersTable';
import { getPrintDetailsParams } from './enspirecycleinfoviews/dataUtil';


export const CycleInfo = ({ handlers }) => {
	const { handleOnNext, handleOnPrevious } = handlers;
	const theme = useTheme();
	const navigate = useNavigate();
	let displayCycleTape = false;
	const screenWidth = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const is1024Width = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
	if (screenWidth || is1024Width) {
		displayCycleTape = true;
	}
	const { data, cycleTapeDisplayed, stockChartRef } = useCycleInfo();

	const isPerformanceAccess = useMemo(
		() => util.checkAccess('PerformanceMonitorAccess'),
		[]
	);

	useEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigate(`/`);
			});
		}
	}, [isPerformanceAccess]);


	return (
		!!data && (
			<Grid container rowSpacing={2}>
				<Grid size={12} container>
					<Card sx={{ width: '100%' }}>
						<CardContent>
							<Grid item size={12}>
								<NextPrevComponent onNext={handleOnNext} onPrev={handleOnPrevious}>
									<Box display="flex" columnGap={1} flexDirection={'row'}>
										<Box flexGrow={1}>
											<TopSection />
										</Box>
									</Box>
								</NextPrevComponent>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid size={12} container>
					<Card sx={{ width: '100%' }}>
						<CardContent>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: displayCycleTape ? '1fr' : '1fr auto',
									gridTemplateRows: '1fr',
									gridColumnGap: '20px'
								}}>
								<Box flexGrow={1}>
									<Grid container rowGap={1}>
										<Grid size={12}>
											<CriticalParametersTable />
										</Grid>
										<Grid size={12}>
											<TrendsLineChart ref={stockChartRef} />
										</Grid>
									</Grid>
								</Box>
								{cycleTapeDisplayed && (
									<Box>
										<CycleTape />
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		)
	);
};

const EnspireCycleInfo = () => {
	const theme = useTheme();
	const location = useLocation();
	const [, { navigateToLastAccessibleRoute }] = usePrerequisites();
	const navigate = useNavigate();
	const dateTimeFormat = util.getDateTimeFormat();
	const [searchParams, setSearchParams] = useSearchParams();
	const canReprocessXml = true;
	const isPerformanceAccess = useMemo(() => util.checkAccess('PerformanceMonitorAccess'), []);
	const [state, setState] = useSetState({
		processMonitoringValue: false,
		loading: false,
		serialNumber: ''
	});

	const serialNumberRef = useRef('');

	const equipmentFamilyId = useMemo(() => searchParams.get('equipmentFamilyId'), [searchParams]);
	const equipmentId = useMemo(() => searchParams.get('equipmentId'), [searchParams]);
	const id = useMemo(
		() => searchParams.get('id'),
		[searchParams]
	);

	const fetchData = useCallback(() => {
		setState({ loading: true });

		const formData = new FormData();
		formData.append('equipmentId', equipmentId);
		formData.append('id', id);
		formData.append('action', 'load');

		fetch(connectionService.EndoSystemCycleList, { method: 'POST', body: formData })
			.then((res) => res.json())
			.then(({ data }) => {
				serialNumberRef.current = data?.SerialNumber;
				setState({serialNumber: data?.SerialNumber})
			})
			.catch((err) => {
				console.error(err)
				Swal.fire({
					titleText: strings.Error,
					text: strings[`Status_${err?.status}`],
					icon: 'error',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
			})
			.finally(() => setState({ loading: false }));
	}, [id, equipmentId]);

	const onGetPrintDetailsParams = useCallback(
		({ data }) => getPrintDetailsParams({ data, dateTimeFormat, equipmentFamilyId, }),
		[dateTimeFormat, equipmentFamilyId]
	);

	const handleOnLoadNextOrPrev = useCallback(
		({ id, equipmentId, type, isIncrement, equipmentFamilyId }) => {
			const formData = new FormData();
			setState({ loading: true });
			equipmentId = equipmentId?._id || equipmentId;
			formData.append('action', 'otherAction');
			formData.append('otherAction', 'NextPrevHandler');
			formData.append('equipmentId', equipmentId);
			formData.append('id', id);
			formData.append('asArray', false);
			formData.append('type', type);
			formData.append('isIncrement', isIncrement);
			fetch(connectionService.CycleList, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					if (json.data?.length) {
						const { cycle: data = {}, AttachmentCount = 0, SterilizerCycleId='', _id: newId} = json.data[0];
						const {EquipmentId='', SerialNumber = '' } = data[0];
						if (newId) {
							const searchParams = getSearchParams()
							searchParams.set('id', newId);
							searchParams.set('equipmentFamilyId', equipmentFamilyId);
							searchParams.set('equipmentId', EquipmentId);
							searchParams.set('attachmentCount', AttachmentCount);
							setState({serialNumber: SerialNumber});
							serialNumberRef.current = SerialNumber;
							navigate(`${location.pathname}?${searchParams.toString()}`);
						}
					} else {
						let message = isIncrement
							? strings.NextPage.replace('{0}', serialNumberRef.current)
							: strings.PrevPage.replace('{0}', serialNumberRef.current);
						Swal.fire({
							titleText: strings.Info,
							text: message,
							icon: 'info',
							allowOutsideClick: false
						});
					}
				})
				.catch((err) => {
					console.error(err)
					Swal.fire({
						titleText: strings.Error,
						text: strings[`Status_${err?.status}`],
						icon: 'error',
						allowOutsideClick: false
					});
				})
				.finally(() =>
					setState({
						loading: false
					})
				);
		},
		[id]
	);

	const handleOnNext = () => {
		handleOnLoadNextOrPrev({
			id: id,
			equipmentId: equipmentId,
			type: 'endoSystemCycle',
			isIncrement: true,
			serialNumber: state.serialNumber,
			equipmentFamilyId: equipmentFamilyId
		})
	};
	const handleOnPrevious = () => {
		handleOnLoadNextOrPrev({
			id: id,
			equipmentId: equipmentId,
			type: 'endoSystemCycle',
			isIncrement: false,
			serialNumber: state.serialNumber,
			equipmentFamilyId: equipmentFamilyId
		});
	};

	useUniqueEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigateToLastAccessibleRoute(false);
			});
		}
	}, [isPerformanceAccess, id]);

	useUniqueEffect(() => {
		if(equipmentId!=="0")
		{fetchData();}
	}, [id, equipmentId]);

	return (
		<CycleInfoProvider
			value={{
				...state,
				canReprocessXml,
				equipmentFamilyId,
			}}
			type={'endoSystemCycle'}
			id={id}
			onGetPrintDetailsParams={onGetPrintDetailsParams}
		>
			<CycleInfo handlers={{ handleOnNext, handleOnPrevious }} />
		</CycleInfoProvider>
	);
}

export default EnspireCycleInfo;